import React, { useState, useMemo, useCallback } from 'react';
import './AccessorySelectorGrouped.css';

const categoryIcons = {
    uprights: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="6" y1="3" x2="6" y2="19" />
            <line x1="16" y1="3" x2="16" y2="19" />
            <line x1="6" y1="7" x2="16" y2="7" />
            <line x1="4" y1="19" x2="8" y2="19" />
            <line x1="14" y1="19" x2="18" y2="19" />
        </svg>
    ),
    'power-lighting': (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13,2 8,11 12,11 9,20" />
        </svg>
    ),
    'bin-bars': (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="5" x2="20" y2="5" />
            <rect x="4" y="8" width="4" height="5" rx="0.5" />
            <rect x="10" y="8" width="4" height="5" rx="0.5" />
            <line x1="6" y1="5" x2="6" y2="8" />
            <line x1="12" y1="5" x2="12" y2="8" />
        </svg>
    ),
    shelving: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="4" x2="4" y2="18" />
            <line x1="4" y1="7" x2="18" y2="7" />
            <line x1="4" y1="12" x2="18" y2="12" />
            <line x1="4" y1="17" x2="18" y2="17" />
        </svg>
    ),
    drawers: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="16" height="16" rx="1.5" />
            <line x1="3" y1="11" x2="19" y2="11" />
            <line x1="9" y1="6" x2="13" y2="6" />
            <line x1="9" y1="15" x2="13" y2="15" />
        </svg>
    ),
    footrests: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3,10 19,10 19,14" />
            <line x1="3" y1="10" x2="3" y2="18" />
            <line x1="19" y1="14" x2="19" y2="18" />
            <line x1="3" y1="10" x2="8" y2="5" />
        </svg>
    ),
    arms: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="3" width="10" height="8" rx="1" />
            <line x1="14" y1="11" x2="14" y2="13" />
            <polyline points="14,13 8,13 8,17" />
            <circle cx="8" cy="18" r="1.5" />
        </svg>
    ),
    panels: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="16" height="16" rx="1.5" />
            <circle cx="7.5" cy="7.5" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="11" cy="7.5" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="14.5" cy="7.5" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="7.5" cy="11" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="11" cy="11" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="14.5" cy="11" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="7.5" cy="14.5" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="11" cy="14.5" r="0.8" fill="currentColor" stroke="none" />
            <circle cx="14.5" cy="14.5" r="0.8" fill="currentColor" stroke="none" />
        </svg>
    ),
    trolleys: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="4" x2="20" y2="4" />
            <rect x="8" y="4" width="6" height="4" rx="0.5" />
            <line x1="11" y1="8" x2="11" y2="11" />
            <rect x="6" y="11" width="10" height="6" rx="1" />
            <circle cx="8" cy="19" r="1.2" />
            <circle cx="14" cy="19" r="1.2" />
        </svg>
    ),
    other: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 3.5L13 8l2.5 2-5 7 1.5-5.5L9.5 10l5-6.5z" />
        </svg>
    ),
};

const CategoryIcon = ({ id }) => {
    return categoryIcons[id] || null;
};

/**
 * AccessorySelectorGrouped - A grouped selector for accessories
 * Organizes accessories into collapsible categories for easier navigation
 */
const AccessorySelectorGrouped = ({ step, configuration, onSelect, onMultiSelect }) => {
    const selectedValues = Array.isArray(configuration[step.key])
        ? configuration[step.key]
        : [];

    // Lightbox state
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxLabel, setLightboxLabel] = useState('');

    const openLightbox = useCallback((e, image, label) => {
        e.preventDefault();
        e.stopPropagation();
        setLightboxImage(image);
        setLightboxLabel(label);
    }, []);

    const closeLightbox = useCallback(() => {
        setLightboxImage(null);
        setLightboxLabel('');
    }, []);

    // Category definitions - Uprights first as they may be required by other accessories
    const categoryDefinitions = useMemo(() => [
        {
            id: 'none',
            label: 'No Accessories',
            patterns: ['no-accessories'],
            requiresUpright: null
        },
        {
            id: 'uprights',
            label: 'Upright Systems',
            patterns: ['upright'],
            requiresUpright: null  // Uprights themselves don't require uprights
        },
        {
            id: 'power-lighting',
            label: 'Power & Lighting',
            patterns: ['power-bar', 'led-lamp'],
            requiresUpright: 'HURA48'  // Lamps require uprights with horizontal arm
        },
        {
            id: 'bin-bars',
            label: 'Bin Bars',
            patterns: ['bin-bar'],
            requiresUpright: 'HURA'  // Bin bars require standard uprights
        },
        {
            id: 'shelving',
            label: 'Shelving',
            patterns: ['steel-shelf', 'shelf-2leg', 'shelf-4leg'],
            requiresUpright: null  // Under-top shelves don't require uprights, above-top do
        },
        {
            id: 'drawers',
            label: 'Drawers',
            patterns: ['pencil-drawer', 'file-drawer'],
            requiresUpright: null  // Drawers don't require uprights
        },
        {
            id: 'footrests',
            label: 'Footrests',
            patterns: ['footrest'],
            requiresUpright: null  // Footrests don't require uprights
        },
        {
            id: 'arms',
            label: 'Arms & Holders',
            patterns: ['keyboard-arm', 'monitor-arm', 'document-holder'],
            requiresUpright: 'HURA'  // Arms/holders require uprights
        },
        {
            id: 'panels',
            label: 'Panels',
            patterns: ['perforated-panel'],
            requiresUpright: 'HURA'  // Panels require uprights
        },
        {
            id: 'trolleys',
            label: 'Trolleys',
            patterns: ['festoon-trolley'],
            requiresUpright: 'HURA48'  // Festoon trolleys require uprights with horizontal arm
        }
    ], []);

    // Group options by category
    const { categorizedOptions, availableCategories } = useMemo(() => {
        const byCategory = {};

        step.options.forEach(option => {
            let categoryId = 'other';

            for (const cat of categoryDefinitions) {
                if (cat.patterns.some(pattern => option.value.includes(pattern))) {
                    categoryId = cat.id;
                    break;
                }
            }

            if (!byCategory[categoryId]) {
                byCategory[categoryId] = [];
            }
            byCategory[categoryId].push(option);
        });

        // Get available categories (that have options)
        const available = categoryDefinitions.filter(cat =>
            byCategory[cat.id] && byCategory[cat.id].length > 0
        );

        // Add "Other" category if there are uncategorized options
        if (byCategory['other'] && byCategory['other'].length > 0) {
            available.push({
                id: 'other',
                label: 'Other Accessories',
                patterns: []
            });
        }

        return { categorizedOptions: byCategory, availableCategories: available };
    }, [step.options, categoryDefinitions]);

    // Track which categories are expanded
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // Start with all categories expanded if few total options, otherwise just first non-"none"
        const nonNone = availableCategories.filter(c => c.id !== 'none');
        if (step.options.length <= 10) {
            return new Set(nonNone.map(c => c.id));
        }
        return new Set(nonNone.slice(0, 1).map(c => c.id));
    });

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const handleCheckboxChange = (value, checked) => {
        // Special handling for "no-accessories" option
        if (value === 'no-accessories') {
            if (checked) {
                onSelect(step.key, ['no-accessories']);
            } else {
                onSelect(step.key, []);
            }
        } else {
            // If selecting any other accessory, remove "no-accessories" first
            if (selectedValues.includes('no-accessories')) {
                onSelect(step.key, checked ? [value] : []);
            } else {
                onMultiSelect(step.key, value, checked);
            }
        }
    };

    // Count selected items per category
    const getSelectedCount = (categoryId) => {
        const options = categorizedOptions[categoryId] || [];
        return options.filter(opt => selectedValues.includes(opt.value)).length;
    };

    // Determine which upright is required based on selected accessories
    const requiredUpright = useMemo(() => {
        // Priority: HURA48 > HURA (HURA48 includes horizontal arm, more capable)
        let required = null;

        for (const value of selectedValues) {
            // Find which category this value belongs to
            for (const cat of categoryDefinitions) {
                if (cat.patterns.some(pattern => value.includes(pattern))) {
                    if (cat.requiresUpright === 'HURA48') {
                        // HURA48 is the highest priority requirement
                        return 'HURA48';
                    } else if (cat.requiresUpright === 'HURA' && required !== 'HURA48') {
                        required = 'HURA';
                    }
                    break;
                }
            }

            // Special handling for LED lamps and steel shelves (above-top variants)
            if (value.includes('led-lamp')) {
                return 'HURA48';  // Lamps always need horizontal arm
            }
            if (value.includes('steel-shelf')) {
                if (required !== 'HURA48') {
                    required = 'HURA';  // Above-top shelves need uprights
                }
            }
        }

        return required;
    }, [selectedValues, categoryDefinitions]);

    // Get the upright option values that satisfy the requirement
    const getUprightOptions = useMemo(() => {
        const uprightOptions = categorizedOptions['uprights'] || [];
        return {
            HURA: uprightOptions.filter(opt =>
                opt.value === 'upright-2' || opt.value === 'upright-3'
            ),
            HURA48: uprightOptions.filter(opt =>
                opt.value === 'upright-horiz-2' || opt.value === 'upright-horiz-3'
            )
        };
    }, [categorizedOptions]);

    // Check if user has any upright selected
    const hasUprightSelected = useMemo(() => {
        const uprightOptions = categorizedOptions['uprights'] || [];
        return uprightOptions.some(opt => selectedValues.includes(opt.value));
    }, [categorizedOptions, selectedValues]);

    // Check if current upright satisfies requirement
    const uprightSatisfiesRequirement = useMemo(() => {
        if (!requiredUpright) return true;

        const selectedUprights = selectedValues.filter(val => val.includes('upright'));

        if (requiredUpright === 'HURA48') {
            // HURA48 requirement is satisfied by upright-horiz-* variants
            return selectedUprights.some(u => u.includes('upright-horiz'));
        } else if (requiredUpright === 'HURA') {
            // HURA requirement is satisfied by any upright
            return selectedUprights.length > 0;
        }

        return true;
    }, [requiredUpright, selectedValues]);

    // Auto-select upright when required (with warning shown first)
    React.useEffect(() => {
        if (requiredUpright && !uprightSatisfiesRequirement) {
            // Auto-select the appropriate upright after a short delay to show the warning
            const timer = setTimeout(() => {
                const options = requiredUpright === 'HURA48'
                    ? getUprightOptions.HURA48
                    : getUprightOptions.HURA;

                if (options.length > 0 && !selectedValues.includes(options[0].value)) {
                    // Add the first matching upright option
                    const newValues = [...selectedValues.filter(v => !v.includes('upright')), options[0].value];
                    onSelect(step.key, newValues);
                }
            }, 1500);  // 1.5 second delay to let user see the warning

            return () => clearTimeout(timer);
        }
    }, [requiredUpright, uprightSatisfiesRequirement, getUprightOptions, selectedValues, onSelect, step.key]);


    const isNoAccessoriesSelected = selectedValues.includes('no-accessories');

    return (
        <div className="accessory-selector-grouped">
            <p className="step-description">{step.description}</p>

            {/* No Accessories option - always visible at top */}
            {categorizedOptions['none'] && (
                <div className="no-accessories-section">
                    {categorizedOptions['none'].map(option => (
                        <label
                            key={option.value}
                            className={`no-accessories-option ${isNoAccessoriesSelected ? 'selected' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={isNoAccessoriesSelected}
                                onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                            />
                            <span className="option-text">
                                <span className="option-icon">{'\u2713'}</span>
                                <span className="option-label">{option.label}</span>
                            </span>
                        </label>
                    ))}
                </div>
            )}

            {/* Upright Requirement Warning */}
            {requiredUpright && !uprightSatisfiesRequirement && !isNoAccessoriesSelected && (
                <div className="upright-requirement-warning">
                    <div className="warning-icon">{'\u26A0\uFE0F'}</div>
                    <div className="warning-content">
                        <strong>Upright Required</strong>
                        <p>
                            Your selected accessories require {requiredUpright === 'HURA48'
                                ? 'an Upright with Horizontal Arm Assembly (HURA48)'
                                : 'an Upright Assembly (HURA)'}.
                            {' '}Auto-selecting the appropriate upright...
                        </p>
                    </div>
                </div>
            )}

            {/* Category Groups */}
            <div className="category-groups">
                {availableCategories
                    .filter(cat => cat.id !== 'none')
                    .map(category => {
                        const options = categorizedOptions[category.id] || [];
                        const isExpanded = expandedCategories.has(category.id);
                        const selectedCount = getSelectedCount(category.id);

                        return (
                            <div
                                key={category.id}
                                className={`category-group ${isExpanded ? 'expanded' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="category-header"
                                    onClick={() => toggleCategory(category.id)}
                                    disabled={isNoAccessoriesSelected}
                                >
                                    <div className="category-info">
                                        <span className="category-icon"><CategoryIcon id={category.id} /></span>
                                        <span className="category-label">{category.label}</span>
                                        {selectedCount > 0 && (
                                            <span className="selected-badge">{selectedCount}</span>
                                        )}
                                    </div>
                                    <span className={`expand-icon ${isExpanded ? 'open' : ''}`}>
                                        {'\u25BC'}
                                    </span>
                                </button>

                                {isExpanded && !isNoAccessoriesSelected && (
                                    <div className="category-options">
                                        {options.map(option => (
                                            <label
                                                key={option.value}
                                                className={`accessory-option ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedValues.includes(option.value)}
                                                    onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                                                />
                                                <div className="option-content">
                                                    <div className="option-header">
                                                        <span className="option-name">{option.label}</span>
                                                        <div className="option-actions">
                                                            {option.image && (
                                                                <button
                                                                    type="button"
                                                                    className="view-image-btn"
                                                                    onClick={(e) => openLightbox(e, option.image, option.label)}
                                                                    title="View reference image"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <rect x="1" y="2.5" width="14" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                                        <circle cx="5" cy="6" r="1.5" stroke="currentColor" strokeWidth="1"/>
                                                                        <path d="M1 10.5L4.5 7.5L7 10L10.5 6L15 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                    <span>View</span>
                                                                </button>
                                                            )}
                                                            {option.priceModifier > 0 && (
                                                                <span className="option-price">+${option.priceModifier.toFixed(2)}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {option.description && (
                                                        <p className="option-description">{option.description}</p>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {/* Selection Summary */}
            {selectedValues.length > 0 && !isNoAccessoriesSelected && (
                <div className="selection-summary">
                    <span className="summary-label">Selected ({selectedValues.length}):</span>
                    <div className="summary-items">
                        {selectedValues.map(val => {
                            const option = step.options.find(o => o.value === val);
                            return option ? (
                                <span key={val} className="summary-item">{option.label}</span>
                            ) : null;
                        })}
                    </div>
                </div>
            )}

            {/* Image Lightbox */}
            {lightboxImage && (
                <div className="accessory-lightbox-overlay" onClick={closeLightbox}>
                    <div className="accessory-lightbox" onClick={(e) => e.stopPropagation()}>
                        <div className="lightbox-header">
                            <h3 className="lightbox-title">{lightboxLabel}</h3>
                            <button
                                type="button"
                                className="lightbox-close"
                                onClick={closeLightbox}
                            >
                                {'\u2715'}
                            </button>
                        </div>
                        <div className="lightbox-body">
                            <img
                                src={lightboxImage}
                                alt={lightboxLabel}
                                className="lightbox-image"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessorySelectorGrouped;
